import { ReactNode } from 'react'
import { cloneDeep } from 'lodash'

import { FolderColumn, SaturatedFolderColumnOptions } from 'redux/folders/initialState'

import { SubfolderFolderItemType } from 'types/folders'
import { BRACKET_NAME, HUMAN_READABLE_FORMAT, DEPRICATED_FORMAT_NAMES, DEFAULT_DECK_CARD_IMAGE } from 'types/deck'
import { DeckCardInfoType } from 'types/search'

import Icon from 'components/elements/Icon'
import Link from 'components/elements/Link'
import Checkbox from 'components/formElements/Checkbox'
import DeckOptions from '../DeckOptions'
import DragHandle from './DragHandle'
import SelectAllColumnHeader from './SelectAllColumnHeader'
import FolderOptions from '../FolderOptions'

import { colorsToManaString, manaStringToElement } from 'components/misc/customIcons/ManaSymbol/helpers'

import { generateDeckUrl } from 'utils/deckSeo'

import styles from './folderTable.module.scss'

export const AUTO_COLUMNS: FolderColumn[] = [
  'dragHandle',
  'multiSelectCheckbox',
  'name',
  'private',
  'unlisted',
  'theorycrafted',
  'format',
  'edhBracket',
  'colors',
  'updatedAt',
  'controls',
  'controls',
]

export const SORTABLE_HEADERS: FolderColumn[] = [
  'name',
  'format',
  'colors',
  'updatedAt',
  'private',
  'unlisted',
  'theorycrafted',
]

// Classes that will always be applied to the column
export const COLUMN_CLASSES: Partial<Record<FolderColumn, string>> = {
  name: styles.name,
  dragHandle: styles.dragHandle,
  multiSelectCheckbox: styles.selectBox,
}

// Classes that will only be applied to the column if autoColumns is true
export const AUTO_COLUMN_CLASSES: Partial<Record<FolderColumn, string>> = {
  ...COLUMN_CLASSES,
  dragHandle: styles.autoDragHandle,
  private: styles.autoPrivate,
  unlisted: styles.autoUnlisted,
  theorycrafted: styles.autoTheorycrafted,
  format: styles.autoFormat,
  edhBracket: styles.autoBracket,
  colors: styles.autoColors,
  updatedAt: styles.autoUpdatedAt,
}

export const COLUMN_HEADERS: Record<FolderColumn, React.ReactNode> = {
  dragHandle: '',
  multiSelectCheckbox: <SelectAllColumnHeader />,
  name: 'Name',
  size: 'Deck size',
  private: 'Private',
  unlisted: 'Unlisted',
  theorycrafted: 'Theorycrafted',
  format: 'Format',
  edhBracket: 'EDH Bracket',
  colors: 'Colors',
  updatedAt: 'Updated at',
  createdAt: 'Created at',
  tags: 'Deck Tags',
  controls: 'Menu',
}

export const generateSaturatedColumns = (
  deckOrFolder: DeckCardInfoType | SubfolderFolderItemType,
  deckOrFolderType: 'deck' | 'folder',
  selected?: boolean,
  onToggleSelected?: () => void,
  autoColumns?: boolean,
): Partial<Record<FolderColumn, SaturatedFolderColumnOptions>> => {
  let controls: ReactNode = null

  if (deckOrFolderType === 'deck' && deckOrFolder.colors) controls = <DeckOptions deck={deckOrFolder} />
  else if (deckOrFolderType === 'folder' && !deckOrFolder.colors) controls = <FolderOptions folder={deckOrFolder} />

  const columnData: Record<FolderColumn, SaturatedFolderColumnOptions> = {
    name: {
      content:
        deckOrFolderType === 'deck' ? (
          <Link href={generateDeckUrl(deckOrFolder?.id || 0, deckOrFolder?.name)}>
            <div className={styles.indicator}>
              <img
                src={deckOrFolder.customFeatured || deckOrFolder.featured || DEFAULT_DECK_CARD_IMAGE}
                alt="deck preview"
              />
            </div>

            <span className={styles.ellipsis}>{deckOrFolder.name}</span>
          </Link>
        ) : (
          <Link href={`/folders/${deckOrFolder?.id}/`}>
            <Icon name={!deckOrFolder.name ? 'level up alternate' : 'folder open outline'} />
            <span className={styles.ellipsis}>{deckOrFolder.name || 'Parent folder'}</span>
          </Link>
        ),
    },
    size: {
      content: <span>{deckOrFolderType === 'folder' ? null : deckOrFolder.size || 0}</span>,
    },
    private: {
      content: <span>{deckOrFolder.private ? 'Private' : null}</span>,
    },
    unlisted: {
      content: <span>{deckOrFolder.unlisted ? 'Unlisted' : null}</span>,
    },
    theorycrafted: {
      content: <span>{deckOrFolder.theorycrafted ? 'Theorycrafted' : null}</span>,
    },
    format: {
      content: (
        <span>
          {deckOrFolderType === 'folder' || deckOrFolder.deckFormat == undefined
            ? null
            : HUMAN_READABLE_FORMAT[deckOrFolder.deckFormat] ||
              DEPRICATED_FORMAT_NAMES[deckOrFolder.deckFormat] ||
              'Package'}
        </span>
      ),
    },
    edhBracket: {
      content: (
        <span>{deckOrFolderType === 'folder' ? null : BRACKET_NAME[deckOrFolder.edhBracket || -1] || null}</span>
      ),
    },
    colors: {
      content: (
        <span>
          {deckOrFolderType === 'folder'
            ? null
            : manaStringToElement(colorsToManaString(deckOrFolder.colors || {})) || null}
        </span>
      ),
    },
    updatedAt: {
      content: <span>{deckOrFolder.updatedAt ? new Date(deckOrFolder.updatedAt).toDateString() : null}</span>,
    },
    createdAt: {
      content: <span>{deckOrFolder.createdAt ? new Date(deckOrFolder.createdAt).toDateString() : null}</span>,
    },
    tags: {
      content: (
        <span className={styles.tags} title={deckOrFolder.tags?.map(tag => tag.name).join(', ')}>
          {deckOrFolder.tags?.map(tag => <span key={`${tag.id}-${tag.position}`}>{tag.name}</span>)}
        </span>
      ),
    },
    // @ts-ignore
    dragHandle: deckOrFolderType === 'deck' ? <DragHandle deck={deckOrFolder} /> : <DragHandle folder={deckOrFolder} />,
    multiSelectCheckbox: deckOrFolder.name ? <Checkbox checked={selected} onChange={onToggleSelected} /> : null,
    controls: controls,
  }

  const response: Partial<Record<FolderColumn, SaturatedFolderColumnOptions>> = {}

  // ts is dumb here and you can't type this inline
  let key: FolderColumn

  for (key in columnData) {
    const column = columnData[key]

    if (column === null) {
      response[key] = null

      continue
    }

    // @ts-ignore
    const content = column?.content || column

    // @ts-ignore
    const onClick = column?.onClick || undefined

    // @ts-ignore
    const className = (autoColumns ? AUTO_COLUMN_CLASSES[key] : COLUMN_CLASSES[key]) || undefined

    // @ts-ignore
    response[key] = { content, className, onClick }
  }

  return response
}

export const generateSaturatedHeaders = (
  handleChangeSort: (column: FolderColumn, priceSource?: string) => void,
  currentSort: string,
  currentDirection: 'ascending' | 'descending',
  autoColumns?: boolean,
): Partial<Record<FolderColumn, SaturatedFolderColumnOptions>> => {
  const response: Partial<Record<FolderColumn, SaturatedFolderColumnOptions>> = {}

  // ts is dumb here and you can't type this inline
  let key: FolderColumn

  for (key in COLUMN_HEADERS) {
    // Neccecary to clone the key here because the arument is being passed by reference to key, whichc is always the last item in the loop
    const columnKey = cloneDeep(key)

    // Appending extra data to the column
    let onClick = SORTABLE_HEADERS.includes(key) ? () => handleChangeSort(columnKey) : undefined

    const className = (autoColumns ? AUTO_COLUMN_CLASSES[key] : COLUMN_CLASSES[key]) || undefined
    const content = (
      <>
        {COLUMN_HEADERS[key]}
        {key === currentSort && (
          <Icon name={currentDirection === 'ascending' ? 'sort amount up' : 'sort amount down'} />
        )}
      </>
    )

    // @ts-ignore
    response[key] = { content, className, onClick }
  }

  return response
}
